import { Component, EventEmitter, inject, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalService, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { skip, Subject, takeUntil } from 'rxjs';
import { ArticleBatchesService } from 'src/app/private/services/article-batches.service';
import { FinalProductService } from 'src/app/private/services/final-product.service';
import { ArticleBatchDto } from 'src/app/shared/dto/article-batch.dto';
import { ArticleDto } from 'src/app/shared/dto/article.dto';
import { FinalProductDto } from 'src/app/shared/dto/final-product.dto';
import { PackagingTypeDto } from 'src/app/shared/dto/packaging-type.dto';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';
import { ProductionOrderStatesEnum } from 'src/app/shared/enums/production-order-states.enum';
import { ModalSelectArticlePackagingComponent } from '../modal-select-article-packaging/modal-select-article-packaging.component';
import { ArticlesService } from './../../../services/articles.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';

interface ArticleOrBatchQuantity {
    articleId?: number;
    batchId?: number;
    quantity: number;
}

@Component({
    selector: 'app-modal-select-final-products',
    standalone: true,
    imports: [
        //TODOfinalProduct: remove useless imports
        NzInputDirective,
        ReactiveFormsModule,
        FormsModule,
        NzTableComponent,
        NzTheadComponent,
        NzTrDirective,
        NzTableCellDirective,
        NzThMeasureDirective,
        NzTbodyComponent,
        NzSelectComponent,
        NzOptionComponent,
        NzMessageModule,
        NzModalTitleDirective,
        NzModalFooterDirective,
        NzCheckboxModule,
        NzDividerModule,
        NzInputGroupComponent,
        NzIconDirective,
        NzRowDirective,
        NzColDirective,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './modal-select-final-products.component.html',
    styleUrl: './modal-select-final-products.component.scss'
})
export class ModalSelectFinalProductsComponent implements OnInit, OnDestroy {
    @Output() emitService = new EventEmitter();

    isLoading: boolean = true;
    isSaving: boolean = false;

    productionOrderProcess: ProductionOrderProcessDto;
    articles: ArticleDto[] = [];
    articlesSelected: ArticleDto[] = [];
    articleBatches: ArticleBatchDto[] = [];
    batchesOfArticlesSelected: Record<number, ArticleBatchDto[]> = {};
    selectedBatchesByArticleId: Record<number, ArticleBatchDto[]> = {};
    quantitiesToProduce: Record<number, ArticleOrBatchQuantity[]> = {};
    fieldsReadOnly: boolean = false;
    searchBatch: Record<number, string> = {};
    showOnlySelectedBatches: Record<number, boolean> = {};

    private finalProducts: FinalProductDto[] = [];

    private readonly unsubscribe$ = new Subject<void>();
    private readonly modalService = inject(NzModalService);
    private readonly toast = inject(NzMessageService);
    private readonly articleService = inject(ArticlesService);
    private readonly articleBatchService = inject(ArticleBatchesService);
    private readonly finalProductService = inject(FinalProductService);

    constructor(@Inject(NZ_MODAL_DATA) public data: any) {
        this.productionOrderProcess = data?.productionOrderProcess;
        if (this.productionOrderProcess.status?.name && this.productionOrderProcess.status?.name !== ProductionOrderStatesEnum.AWAITING) {
            this.fieldsReadOnly = true;
        }

        this.articles = data?.articles;
        this.articleBatches = data?.articlesBatches;
        this.finalProducts = data?.productionOrderProcess?.finalProducts;
    }

    async ngOnInit(): Promise<void> {
        this.getArticlesFromFinalProducts();

        await this.articleService.setupWebSocket();
        this.articleService.articles$
            .pipe(
                takeUntil(this.unsubscribe$),
                skip(1)
            )
            .subscribe({
                next: (articles) => {
                    this.articles = articles;
                },
                error: () => {
                    this.toast.error(`Error al obtener los artículos`);
                }
            });

        await this.articleBatchService.setupWebSocket();
        this.articleBatchService.articleBatches$
            .pipe(
                takeUntil(this.unsubscribe$),
                skip(1)
            )
            .subscribe({
                next: (articleBatches) => {
                    this.articleBatches = articleBatches;
                    this.updateAllArticleRelatedFields();
                },
                error: () => {
                    this.toast.error(`Error al obtener los lotes de artículos`);
                }
            });
        
        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.articleService.disconnectWebSocket();
        this.articleBatchService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    compareById(o1: any, o2: any): boolean {
        return o1 && o2 ? o1.id === o2.id : o1 === o2;
    }

    addRow() {
        this.articlesSelected[this.articlesSelected.length] = new ArticleDto();
    }

    getQuantityToProduce(articleId: number, batchId: number): number {
        const batchQuantity = this.quantitiesToProduce[articleId]?.find(q => q.batchId === batchId);
        return batchQuantity ? batchQuantity.quantity : 0;
    }

    setQuantityToProduce(articleId: number, batch: ArticleBatchDto, quantity: number): void {
        const batchQuantity = this.quantitiesToProduce[articleId]?.find(q => q.batchId === batch.id);
        if (batchQuantity) {
            batchQuantity.quantity = quantity;
        }
        this.clearPackagings(undefined, batch);
    }

    removeSelectedArticle(index: number) {
        const articleSelected = this.articlesSelected[index];
        this.finalProducts = this.finalProducts.filter(finalProduct => finalProduct.article?.id !== articleSelected.id);
        this.updateArticleRelatedFields(undefined, articleSelected);
        this.articlesSelected.splice(index, 1);
    }

    updateArticleRelatedFields(articleSelected?: ArticleDto, previousArticleSelected?: ArticleDto) {
        try {
            if (previousArticleSelected?.id) {
                delete this.batchesOfArticlesSelected[previousArticleSelected.id];
                delete this.selectedBatchesByArticleId[previousArticleSelected.id];
                delete this.quantitiesToProduce[previousArticleSelected.id];
                delete this.showOnlySelectedBatches[previousArticleSelected.id];
            }

            if (articleSelected) {
                this.batchesOfArticlesSelected[articleSelected.id] = [];
                this.quantitiesToProduce[articleSelected.id] = this.quantitiesToProduce[articleSelected.id] ?? [];
                this.showOnlySelectedBatches[articleSelected.id] = false;

                if (articleSelected && articleSelected.useBatches) {
                    const batches = this.articleBatches.filter(articleBatch => articleBatch.article!.id === articleSelected.id);
                    this.selectedBatchesByArticleId[articleSelected.id] = [];

                    if (batches && batches.length > 0) {
                        batches.forEach((batch) => {
                            const quantityToProduce = this.finalProducts.find(finalProduct => finalProduct.articleBatch?.id === batch.id)?.quantityToProduce;
                            this.batchesOfArticlesSelected[articleSelected.id] = [...this.batchesOfArticlesSelected[articleSelected.id], batch];

                            const batchIdsInQuantitiesToProduce = this.quantitiesToProduce[articleSelected.id].map(qp => qp.batchId);
                            if (!batchIdsInQuantitiesToProduce.includes(batch.id!)) {
                                this.quantitiesToProduce[articleSelected.id] = [
                                    ...this.quantitiesToProduce[articleSelected.id] as ArticleOrBatchQuantity[],
                                    { batchId: batch.id!, quantity: quantityToProduce ?? 0 }
                                ];
                            }

                            const finalProductLinkedBatch = this.finalProducts.find(finalProduct => finalProduct.articleBatch?.id === batch.id);
                            if (finalProductLinkedBatch) {
                                batch.packagings = finalProductLinkedBatch.packagings;
                                this.selectedBatchesByArticleId[articleSelected.id] = [...this.selectedBatchesByArticleId[articleSelected.id], batch];
                            }
                        });
                    }
                } else if (articleSelected! && !articleSelected.useBatches) {
                    const finalProductLinkedAticle = this.finalProducts.find(finalProduct => (finalProduct.article?.id || finalProduct.articleId) === articleSelected.id);
                    if (finalProductLinkedAticle) {
                        articleSelected.packagings = finalProductLinkedAticle.packagings;
                    }
                    const quantityToProduce = this.finalProducts.find(finalProduct => (finalProduct.article?.id || finalProduct.articleId) === articleSelected.id)?.quantityToProduce;
                    this.quantitiesToProduce[articleSelected.id] = [{ articleId: articleSelected.id, quantity: quantityToProduce ?? 0 }];
                }
            }

        } catch (error) {
            this.toast.error(`Error al actualizar los campos relacionados con el artículo`);
        }
    }

    onSearchBatch(event: string, articleId: number) {
        try {
            this.updateAllArticleRelatedFields();
            this.batchesOfArticlesSelected[articleId] = this.batchesOfArticlesSelected[articleId].filter(batch => batch.batchNumber?.includes(event));
        } catch (error) {
            this.toast.error(`Error al filtrar los lotes de artículos`);
        }
    }

    async clearFilterBatch(idx: number, articleId: number): Promise<void> {
        this.searchBatch[idx] = '';
        this.onSearchBatch(this.searchBatch[idx], articleId);
    }

    onAddPackagings(article?: ArticleDto, articleBatch?: ArticleBatchDto, index?: number, subindex?: number) {
        if (!articleBatch && !article) {
            this.toast.error(`No se ha facilitado ni un artículo ni un lote para poder asignar los formatos.`);
            return;
        }

        if (article) {
            const selectedPackagings = this.finalProducts.find(finalProduct => finalProduct.articleId === article.id)?.packagings;
            const modal = this.modalService.create({
                nzContent: ModalSelectArticlePackagingComponent,
                nzStyle: { width: '90%', top: '15%' },
                nzBodyStyle: { height: '50vh' },
                nzClosable: false,
                nzFooter: null,
                nzData: {
                    nzTitle: `Formatos para el artículo ${article.name} - Orden ${this.productionOrderProcess?.id} ${this.productionOrderProcess?.description ? '| ' + this.productionOrderProcess?.description : ''}`,
                    remainingQuantityToProduce: this.quantitiesToProduce[article.id!][0].quantity,
                    selectedPackagings: selectedPackagings && selectedPackagings.length > 0 ? selectedPackagings : article.packagings
                }
            });

            modal.componentInstance?.emitService.subscribe(async (assignedPackagingTypes: PackagingTypeDto[]) => {
                if (!assignedPackagingTypes) {
                    modal.close();

                    return;
                }

                const articleIndex: number = this.articlesSelected.findIndex(cab => cab.id === article!.id);

                const packagingsToCreate: PackagingDto[] = [];

                for (const packagingType of assignedPackagingTypes) {
                    for (let i = 0; i < packagingType.quantity!; i++) {
                        packagingsToCreate.push({
                            packagingTypeId: packagingType.id!,
                            articleId: article.id,
                            consumed: false,
                            packagingType: packagingType
                        });
                    }
                }

                this.articlesSelected[articleIndex].packagings = packagingsToCreate;
                modal.close();
            });
        } else if (articleBatch) {
            const selectedPackagings = this.finalProducts.find(finalProduct => (finalProduct.articleBatchId || finalProduct.articleBatch?.id) === articleBatch.id)?.packagings;
            const modal = this.modalService.create({
                nzContent: ModalSelectArticlePackagingComponent,
                nzStyle: { width: '90%', top: '15%' },
                nzBodyStyle: { height: '50vh' },
                nzClosable: false,
                nzFooter: null,
                nzData: {
                    nzTitle: `Formatos para el lote ${articleBatch.batchNumber} - Orden ${this.productionOrderProcess?.id || 'nueva'} ${this.productionOrderProcess?.description ? '| ' + this.productionOrderProcess?.description : ''}`,
                    remainingQuantityToProduce: this.quantitiesToProduce[articleBatch.article!.id!][subindex!].quantity,
                    selectedPackagings: selectedPackagings && selectedPackagings.length > 0 ? selectedPackagings : articleBatch.packagings
                }
            });

            modal.componentInstance?.emitService.subscribe(async (assignedPackagingTypes: PackagingTypeDto[]) => {
                if (!assignedPackagingTypes) {
                    modal.close();

                    return;
                }

                const batchIndex: number = this.articleBatches.findIndex(cab => cab.id === articleBatch.id);

                const packagingsToCreate: PackagingDto[] = [];

                for (const packagingType of assignedPackagingTypes) {
                    for (let i = 0; i < packagingType.quantity!; i++) {
                        packagingsToCreate.push({
                            packagingTypeId: packagingType.id!,
                            articleId: articleBatch.articleId,
                            articleBatchId: articleBatch.id!,
                            consumed: false,
                            packagingType: packagingType
                        });
                    }
                }
                this.articleBatches[batchIndex].packagings = packagingsToCreate;
                this.batchesOfArticlesSelected[articleBatch.article!.id!][subindex!] = this.articleBatches[batchIndex];
                //TODO packaging: if the finalProduct is new, it will not be in the finalProducts array and the packagings will not be assigned
                const linkedFinalProduct = this.finalProducts.find(fp => fp.articleBatch?.id === articleBatch.id);
                if (linkedFinalProduct) {
                    this.finalProducts.find(fp => fp.articleBatch?.id === articleBatch.id)!.packagings = packagingsToCreate;
                }
                modal.close();
            });
        }
    }

    async onGenerateNewBatch(index: number) {
        try {
            const newBatch: ArticleBatchDto = {
                quantity: 0,
                articleId: this.articlesSelected[index]?.id as number,
                quantityPerProcess: 0
            };

            const batchCreated = await this.articleBatchService.createArticleBatch(newBatch);
            this.toast.success(`Lote ${batchCreated.batchNumber} creado correctamente`);
        } catch (e: any) {
            this.toast.error(`Error al crear lote. Error: ${e.message}`);
        }
    }

    setAsSelected(articleId: number, subindex: number) {
        const batch = this.batchesOfArticlesSelected[articleId][subindex];

        if (this.selectedBatchesByArticleId[articleId]?.some(b => b.id === batch.id)) {
            this.selectedBatchesByArticleId[articleId] = this.selectedBatchesByArticleId[articleId].filter(b => b.id !== batch.id);
        } else {
            this.selectedBatchesByArticleId[articleId] = this.selectedBatchesByArticleId[articleId] ?? [];
            this.selectedBatchesByArticleId[articleId].push(batch);
        }
    }

    isBatchSelected(articleId: number, batchId: number): boolean {
        return this.selectedBatchesByArticleId[articleId]?.some(b => b.id === batchId) ?? false;
    }

    handleCancel() {
        this.modalService.closeAll();
    }

    clearPackagings(article?: ArticleDto, batch?: ArticleBatchDto) {
        if (article?.packagings?.length) {
            article.packagings = [];
            this.finalProducts.find(fp => fp.article?.id === article.id)!.packagings = [];
            this.toast.info(`Los paquetes asignados al artículo ${article.name} han sido eliminados`);
        } else if (batch?.packagings?.length) {
            batch.packagings = [];
            this.finalProducts.find(fp => fp.articleBatch?.id === batch.id)!.packagings = [];
            this.toast.info(`Los paquetes asignados al lote ${batch.batchNumber} han sido eliminados`);
        }
    }

    async save() {
        try {
            this.isSaving = true;

            const finalProducts: FinalProductDto[] = [];

            const articlesSelectedValues = Object.values(this.articlesSelected);
            articlesSelectedValues.forEach(article => {
                if (!article.useBatches) {
                    const quantityToProduce = this.quantitiesToProduce[article.id][0].quantity;

                    if (this.finalProducts.find(fp => fp.article?.id === article.id && !article.packagings)) {
                        article.packagings = this.finalProducts.find(fp => fp.articleBatch?.id === article.id)?.packagings;
                    }
                    finalProducts.push({
                        articleId: article.id,
                        article: article,
                        packagings: article.packagings ?? undefined,
                        quantityToProduce: quantityToProduce,
                    });
                }
            });

            const selectedBatchesId = Object.values(this.selectedBatchesByArticleId)
                .flat(2)
                .map(batch => batch.id);

            if (selectedBatchesId.length > 0) {
                const selectedBatches: ArticleBatchDto[] = Object.values(this.batchesOfArticlesSelected)
                    .flat()
                    .filter(batch => selectedBatchesId.includes(batch.id));

                for (const batch of selectedBatches) {
                    let quantityToProduce = 0;
                    Object.values(this.quantitiesToProduce).forEach(qp => {
                        const quantity = (qp as ArticleOrBatchQuantity[]).find(q => q.batchId === batch.id)?.quantity;

                        if (quantity) {
                            quantityToProduce = quantity;
                        }
                    });

                    if (this.finalProducts.find(fp => fp.articleBatch?.id === batch.id && !batch.packagings)) {
                        batch.packagings = this.finalProducts.find(fp => fp.articleBatch?.id === batch.id)?.packagings;
                    }

                    finalProducts.push({
                        articleBatchId: batch.id,
                        articleBatch: batch,
                        article: batch.article,
                        articleId: batch.articleId,
                        packagings: batch.packagings ?? undefined,
                        quantityToProduce: quantityToProduce,
                    });
                }
            }

            if (!this.validate(finalProducts)) {
                this.isSaving = false;
                return;
            }

            if (this.productionOrderProcess.id) {
                const createdFinalProducts = await this.finalProductService.setFinalProductsToOrderProcess(this.productionOrderProcess.id, finalProducts);
                this.toast.success(`Productos finales guardados correctamente`);
                this.emitService.next(createdFinalProducts);
            } else {
                this.emitService.next(finalProducts);
            }

            this.isSaving = false;
            this.modalService.closeAll();
        } catch (error) {
            this.isSaving = false;
            this.toast.error(`Error al guardar los productos finales`);
        }
    }

    isDisabled(articleId: number): boolean {
        const existingArticle: ArticleDto | undefined = this.articlesSelected.find(as => as.id === articleId);

        return existingArticle !== undefined;
    }

    private validate(finalProducts: FinalProductDto[]): boolean {

        if (finalProducts.length === 0) {
            this.toast.error(`No se han seleccionado artículos para producir`);
            return false;
        }

        for (const finalProduct of finalProducts) {

            if (finalProduct.quantityToProduce <= 0) {
                this.toast.error(`La cantidad a producir del ${finalProduct.articleBatch ? 'lote' : 'artículo'} ${finalProduct.articleBatch ? finalProduct.articleBatch.batchNumber : finalProduct.article?.name} no puede ser menor o igual a 0`);
                return false;
            }

            if (finalProduct.article?.useBatches && finalProduct.articleBatch?.packagings && finalProduct.articleBatch?.packagings.length > 0) {
                if (!this.validatePackagings(finalProduct.articleBatch.packagings, finalProduct.quantityToProduce, finalProduct.articleBatch.batchNumber!, finalProduct.article.name)) {
                    return false;
                }
            } else if (!finalProduct.article?.useBatches && finalProduct.article?.packagings && finalProduct.article?.packagings.length > 0) {
                if (!this.validatePackagings(finalProduct.article.packagings, finalProduct.quantityToProduce, finalProduct.article.name)) {
                    return false;
                }
            }
        }
        return true;
    }

    private validatePackagings(packagings: PackagingDto[], quantity: number, batchNumberOrArticleName: string = '', articleName?: string): boolean {
        const totalPackagingsQuantity: number = packagings.reduce((a, b) => a + b.packagingType!.quantityPerPackaging, 0);

        if (totalPackagingsQuantity < quantity) {
            this.toast.error(`La cantidad total de los paquetes (${totalPackagingsQuantity}) asignados al ${articleName ? 'artículo' : 'lote'} ${batchNumberOrArticleName} es menor a la cantidad a producir (${quantity})`);
            return false;
        }

        const maxQuantityPerPackaging = Math.max(...packagings.map(p => p.packagingType!.quantityPerPackaging));

        if (totalPackagingsQuantity - quantity > maxQuantityPerPackaging) {
            this.toast.error(`Exedente de capacidad de paquetes para el ${articleName ? 'artículo' : 'lote'} ${articleName || batchNumberOrArticleName}. Exceso de: ${(totalPackagingsQuantity - quantity)}`);
            return false;
        }

        return true;
    }

    private updateAllArticleRelatedFields() {
        try {
            for (const articleSelected of this.articlesSelected) {
                this.updateArticleRelatedFields(articleSelected);
            }
        } catch (error) {
            this.toast.error(`Error al actualizar todos los campos relacionados con los artículos`);
        }
    }

    private getArticlesFromFinalProducts() {
        const articleIds = this.finalProducts.map(finalProduct => finalProduct.articleBatch?.article?.id || finalProduct.article!.id);
        const uniqueArticleIds = Array.from(new Set(articleIds));


        uniqueArticleIds.forEach((articleId, index) => {
            const articleSelected = this.articles.find(article => article.id === articleId)!;
            this.articlesSelected.push(articleSelected);
        });

        this.updateAllArticleRelatedFields();
    }
}
